<template>
    <div class="prime-form-container">
        <!-- Modals for Terms and Privacy Policy -->
        <b-modal id="modal-privacy" header-class="p-0" footer-class="d-flex justify-content-center border-0"
            content-class="custom-model-wrap" :style="themeColor">
            <template #modal-header="{ cancel }">
                <div class="title-block text-center p-2">
                    {{ $translate('privacy_policy') }}
                </div>
                <button class="close" @click="cancel()">×</button>
            </template>
            <template #default>
                <div class="modal-content-text">
                    <span v-html="companySettingData.privacy_policy"></span>
                </div>
            </template>
            <template #modal-footer="{ cancel }">
                <button class="modal-close-button" @click="cancel()">
                    {{ $translate('close') }}
                </button>
            </template>
        </b-modal>

        <b-modal id="modal-term" header-class="p-0" footer-class="d-flex justify-content-center border-0"
            content-class="custom-model-wrap" :style="themeColor">
            <template #modal-header="{ cancel }">
                <div class="title-block text-center p-2">
                    {{ $translate('terms_of_service') }}
                </div>
                <button class="close" @click="cancel()">×</button>
            </template>
            <template #default>
                <div class="modal-content-text">
                    <span v-html="companySettingData.term_condition"></span>
                </div>
            </template>
            <template #modal-footer="{ cancel }">
                <button class="modal-close-button" @click="cancel()">
                    {{ $translate('close') }}
                </button>
            </template>
        </b-modal>

        <form @submit.prevent="change_current_step" class="prime-form">
            <!-- Profile Image Section -->
            <div class="profile-image-section">
                <link-modal-customer-sign-up v-model="model.profile"></link-modal-customer-sign-up>
            </div>

            <!-- Customer Type Field -->
            <PrimeSelect
                v-model="model.type_id"
                :label="$translate('i_am') || 'I am'"
                :placeholder="$translate('select_customer_type') || 'Select Customer Type'"
                :options="customerTypes"
                value-field="type_id"
                label-field="name"
                required
                v-custom-validation="$selectRequired()"
            />

            <!-- Company Name Field (if type is company) -->
            <PrimeInput
                v-if="model.type_id == 2"
                v-model="model.customer_name"
                :label="$translate('customer_name') || 'Customer Name'"
                :placeholder="$translate('enter_name') || 'Enter name'"
                required
                v-custom-validation="$textRequired()"
            />

            <!-- First Name & Last Name Fields (if type is individual) -->
            <div v-else class="form-row">
                <PrimeInput
                    v-model="model.first_name"
                    :label="$translate('first_name') || 'First Name'"
                    :placeholder="$translate('enter_first_name') || 'Enter first name'"
                    required
                    v-custom-validation="$textRequired()"
                />
                
                <PrimeInput
                    v-model="model.last_name"
                    :label="$translate('last_name') || 'Last Name'"
                    :placeholder="$translate('enter_last_name') || 'Enter last name'"
                    required
                    v-custom-validation="$textRequired()"
                />
            </div>

            <!-- Email & Phone Row -->
            <div class="form-row">
                <PrimeInput
                    v-model="model.email"
                    type="email"
                    :label="$translate('email_address') || 'Email Address'"
                    :placeholder="$translate('enter_email') || 'Enter email'"
                    icon="envelope"
                    required
                    v-custom-validation="$emailRequired()"
                />

                <PrimeInput
                    v-model="model.phone"
                    type="tel"
                    :label="$translate('phone_number') || 'Phone Number'"
                    :placeholder="$translate('phone_number_placeholder') || 'Enter phone number'"
                    icon="phone"
                    required
                    v-custom-validation="$textRequired()"
                />
            </div>

            <!-- Country Field -->
            <PrimeSelect
                v-model="model.country_code"
                :label="$translate('country') || 'Country'"
                :placeholder="$translate('select_country') || 'Select Country'"
                :options="countries"
                value-field="value"
                label-field="text"
                :required="customerAddressRule == 1"
                v-custom-validation="$selectRequired()"
            />

            <!-- Address Fields Row -->
            <div class="form-row">
                <PrimeInput
                    v-model="model.address1"
                    :label="$translate('address_line_1') || 'Address Line 1'"
                    :placeholder="$translate('address') || 'Enter address'"
                    icon="map-marker"
                    :required="customerAddressRule == 1"
                    v-custom-validation="$textRequired()"
                />

                <PrimeInput
                    v-model="model.address2"
                    :label="$translate('address_line_2') || 'Address Line 2'"
                    :placeholder="$translate('address') || 'Enter address'"
                />
            </div>

            <!-- City, State, Zip Row -->
            <div class="form-row">
                <PrimeInput
                    v-model="model.city"
                    :label="$translate('city') || 'City'"
                    placeholder="City"
                    :required="customerAddressRule == 1"
                    v-custom-validation="$textRequired()"
                />

                <PrimeSelect
                    v-model="model.state_name"
                    :label="$translate('state') || 'State'"
                    :placeholder="$translate('select_state') || 'Select State'"
                    :options="states"
                    value-field="text"
                    label-field="text"
                    :required="customerAddressRule == 1"
                    v-custom-validation="$selectRequired()"
                />

                <PrimeInput
                    v-model="model.zip_code"
                    :label="$translate('zip_code') || 'Zip Code'"
                    :placeholder="$translate('zip_code') || 'Zip Code'"
                    v-custom-validation="$textRequired()"
                />
            </div>

            <!-- Terms & Conditions -->
            <PrimeCheckbox
                v-model="model.agreed_to_terms"
                :true-value="1"
                :false-value="0"
            >
                {{ $translate('i_agree_to') || 'I agree to' }} {{ companySettingData.name }}
                <a href="javascript:void(0);" @click="open_terms()" class="terms-link">
                    {{ $translate('terms_of_service') || 'Terms of Service' }}
                </a>
                {{ $translate('and') || 'and' }}
                <a href="javascript:void(0);" @click="open_privacy()" class="terms-link">
                    {{ $translate('privacy_policy') || 'Privacy Policy' }}
                </a>
            </PrimeCheckbox>

            <!-- Submit Buttons -->
            <div class="form-actions">
                <PrimeButton
                    type="submit"
                    :label="$translate('next') || 'Next'"
                    icon-right="arrow-right"
                />
                <PrimeButton
                    type="button"
                    variant="secondary"
                    :label="$translate('cancel') || 'Cancel'"
                />
            </div>
        </form>
    </div>
</template>

<script>
import permissionsMixins from '@/mixins/permissionsMixins'
import LinkModalCustomerSignUp from '@/components/LinkModalCustomerSignUp'
import PrimeInput from '@/components/ui/PrimeInput'
import PrimeSelect from '@/components/ui/PrimeSelect'
import PrimeCheckbox from '@/components/ui/PrimeCheckbox'
import PrimeButton from '@/components/ui/PrimeButton'

export default {
    name: 'customerUsers-profile',
    mixins: [permissionsMixins],
    components: {
        LinkModalCustomerSignUp,
        PrimeInput,
        PrimeSelect,
        PrimeCheckbox,
        PrimeButton
    },
    data() {
        return {
            customerTypes: [
                { type_id: 1, name: this.$translate('individual') || 'Individual' },
                { type_id: 2, name: this.$translate('company') || 'Company' },
            ],
            loading: false,
            hasErrors: false,
        }
    },
    props: ['value', 'themeColor', 'companySettingData'],
    created() {
        this.$store.dispatch('locations/fetchCountry')
    },
    computed: {
        companyPolicyKey() {
            return this.$store.state.company.settingKeys.companyPolicyKey
        },
        companyPolicyValue() {
            const companyPolicy = this.$store.getters['company/companyPolicyValue']
            return companyPolicy
        },
        termsOfServiceDocumentUrl() {
            const url = this.companyPolicyValue && this.companyPolicyValue.terms_of_service
            return url
        },
        privacyPolicyDocumentUrl() {
            const url = this.companyPolicyValue && this.companyPolicyValue.privacy_policy
            return url
        },
        model: {
            get() {
                return this.value
            },
        },
        countries() {
            return this.$store.state.locations.country
        },
        states() {
            return this.$store.state.locations.states
        },
        customerAddressRuleKey() {
            return this.$store.state.company.settingKeys.customerAddressRuleKey;
        },
        customerAddressRule() {
            return this.$store.getters["company/customerAddressRule"];
        },
    },
    methods: {
        change_current_step() {
            this.$emit('next-clicked');
        },
        submitForm() {
            this.$emit('submitForm');
        },
        open_privacy() {
            this.$bvModal.show('modal-privacy')
        },
        open_terms() {
            this.$bvModal.show('modal-term')
        }
    },
    watch: {
        'model.country_code': {
            immediate: true,
            handler: async function (val) {
                await this.$store.dispatch('locations/fetchstate', val)
            },
        },
    },
}
</script>

<style scoped>
@import url('~primeicons/primeicons.css');

.prime-form-container {
    max-width: 100%;
    margin: 0 auto;
}

.prime-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.profile-image-section {
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.form-label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 4px;
}

.required-asterisk {
    color: #ef4444;
    font-weight: 500;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.form-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 400;
    color: #374151;
    background: white;
    transition: all 0.2s ease;
}

.form-input.with-icon {
    padding-left: 44px;
}

.form-input:focus {
    outline: none;
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.form-input::placeholder {
    color: #9ca3af;
}

.input-icon {
    position: absolute;
    left: 14px;
    color: #6b7280;
    font-size: 16px;
    z-index: 1;
}

.select-wrapper {
    position: relative;
}

.form-select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 400;
    color: #374151;
    background: white;
    appearance: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.form-select:focus {
    outline: none;
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 14px;
    pointer-events: none;
}

.checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.form-checkbox {
    display: none;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.5;
    color: #374151;
}

.checkbox-indicator {
    width: 20px;
    height: 20px;
    border: 2px solid #d1d5db;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-top: 2px;
}

.form-checkbox:checked + .checkbox-label .checkbox-indicator {
    background: #2F80ED;
    border-color: #2F80ED;
}

.form-checkbox:checked + .checkbox-label .checkbox-indicator::after {
    content: '✓';
    color: white;
    font-size: 12px;
    font-weight: 600;
}

.checkbox-text {
    flex: 1;
}

.terms-link {
    color: #2F80ED;
    text-decoration: none;
    font-weight: 500;
}

.terms-link:hover {
    color: #56CCF2;
    text-decoration: underline;
}

.form-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 16px;
}

.submit-button {
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.submit-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(47, 128, 237, 0.3);
}

.cancel-button {
    background: #6b7280;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.cancel-button:hover {
    background: #4b5563;
    transform: translateY(-1px);
}

.submit-button:active,
.cancel-button:active {
    transform: translateY(0);
}

/* Modal Styles */
.modal-content-text {
    padding: 20px 0;
    max-height: 400px;
    overflow-y: auto;
}

.modal-close-button {
    background: #2F80ED;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
}

.modal-close-button:hover {
    background: #56CCF2;
}

/* Responsive Design */
@media (max-width: 768px) {
    .form-row {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .prime-form {
        gap: 20px;
    }
    
    .form-input, .form-select {
        padding: 12px 14px;
    }
    
    .form-input.with-icon {
        padding-left: 40px;
    }
    
    .input-icon {
        left: 12px;
    }
    
    .form-actions {
        flex-direction: column;
    }
    
    .submit-button,
    .cancel-button {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .checkbox-label {
        font-size: 13px;
    }
}

/* Hide spin buttons for number inputs */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
}
</style>